import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Camera,
  FileText,
  ArrowLeft,
  AlertCircle,
  Maximize2,
  Minimize2,
  X,
  Layers,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Move,
  ExternalLink,
  Calendar,
  ChevronRight,
  EyeOff,
  Ban,
  Sparkles,
  ShieldAlert,
  Info,
  PlusCircle,
  Trash2,
  Edit3,
  Check,
  MousePointer,
} from "lucide-react";
import styles from "./damage-page.module.scss";
import { DashboardApi } from "@api/dashboard/dashboard.api";
import {
  FleetInspectionDetailResponse,
  FleetInspectionDetailItem,
  DamageItem,
} from "@models/dashboard.models";
import { exportFleetInspectionPDF } from "@utils/exportFleetInspectionPDF";
import { formatDateDDMMYYYY } from "@utils/dateUtils";

const ANGLE_CONFIG: { label: string; key: keyof FleetInspectionDetailItem; index: number }[] = [
  { label: "Front", key: "front_view_img", index: 1 },
  { label: "Front-Left", key: "front_left_img", index: 2 },
  { label: "Left Side", key: "left_side_img", index: 3 },
  { label: "Left Side Zoom", key: "left_side_zoom", index: 4 },
  { label: "Rear-Left", key: "rear_left_img", index: 5 },
  { label: "Rear", key: "rear_view_img", index: 6 },
  { label: "Rear-Right", key: "rear_right_img", index: 7 },
  { label: "Right Side", key: "right_side_img", index: 8 },
  { label: "Right Side Zoom", key: "right_side_zoom", index: 9 },
  { label: "Front-Right", key: "front_right_img", index: 10 },
];

const ANGLE_INDEX_MAP: Record<number, { label: string; key: keyof FleetInspectionDetailItem }> = {
  1: { label: "Front", key: "front_view_img" },
  2: { label: "Front-Left", key: "front_left_img" },
  3: { label: "Left Side", key: "left_side_img" },
  4: { label: "Left Side Zoom", key: "left_side_zoom" },
  5: { label: "Rear-Left", key: "rear_left_img" },
  6: { label: "Rear", key: "rear_view_img" },
  7: { label: "Rear-Right", key: "rear_right_img" },
  8: { label: "Right Side", key: "right_side_img" },
  9: { label: "Right Side Zoom", key: "right_side_zoom" },
  10: { label: "Front-Right", key: "front_right_img" },
};

const getAngleImage = (incident: any, key: string): string | undefined => {
  if (!incident) return undefined;
  if (incident[key]) return incident[key];

  if (key === "left_side_zoom") {
    return (
      incident.left_side_zoom ||
      incident.left_zoom_image ||
      incident.left_side_zoom_img ||
      incident.left_zoom ||
      incident.left_zoom_img ||
      incident.zoom_left
    );
  }
  if (key === "right_side_zoom") {
    return (
      incident.right_side_zoom ||
      incident.right_zoom_image ||
      incident.right_side_zoom_img ||
      incident.right_zoom ||
      incident.right_zoom_img ||
      incident.zoom_right
    );
  }
  if (key === "front_view_img") return incident.front_view_img || incident.front_view_image || incident.front_img || incident.front_view;
  if (key === "front_left_img") return incident.front_left_img || incident.front_left_image || incident.front_left;
  if (key === "left_side_img") return incident.left_side_img || incident.left_side_image || incident.left_side;
  if (key === "rear_left_img") return incident.rear_left_img || incident.rear_left_image || incident.rear_left;
  if (key === "rear_view_img") return incident.rear_view_img || incident.rear_view_image || incident.rear_img || incident.rear_view;
  if (key === "rear_right_img") return incident.rear_right_img || incident.rear_right_image || incident.rear_right;
  if (key === "right_side_img") return incident.right_side_img || incident.right_side_image || incident.right_side;
  if (key === "front_right_img") return incident.front_right_img || incident.front_right_image || incident.front_right;

  return undefined;
};

const getPhotosForDamage = (
  dmg: any,
  incident?: FleetInspectionDetailItem | null
): { url: string; label: string; angleLabel?: string }[] => {
  const photos: { url: string; label: string; angleLabel?: string }[] = [];
  if (!dmg) return photos;

  const directUrl = dmg.image_url || dmg.damage_img || dmg.cropped_img || dmg.image || dmg.img || dmg.url;
  if (directUrl && typeof directUrl === "string") {
    photos.push({ url: directUrl, label: "AI Damage Focus Crop" });
  }

  const numbers = dmg.image_numbers || dmg.images || dmg.image_ids || dmg.photo_numbers;
  if (numbers && Array.isArray(numbers) && incident) {
    numbers.forEach((num: any) => {
      const parsedNum = Number(num);
      const mapped = ANGLE_INDEX_MAP[parsedNum];
      if (mapped) {
        const imgUrl = getAngleImage(incident, mapped.key);
        if (imgUrl && !photos.some((p) => p.url === imgUrl)) {
          photos.push({
            url: imgUrl,
            label: `${mapped.label} (#${parsedNum})`,
            angleLabel: mapped.label,
          });
        }
      }
    });
  }

  return photos;
};

const AngleThumbImage: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (!src || hasError) {
    return <Camera size={24} strokeWidth={1.5} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      referrerPolicy="no-referrer"
      style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#0f172a", borderRadius: "8px" }}
      onError={() => setHasError(true)}
    />
  );
};

export interface ActiveMarkerItem {
  damageIndex: number;
  damageId?: string | number;
  damageType: string;
  severity: string;
  location: string;
  point?: { x: number; y: number };
  boundingBox?: { x: number; y: number; width: number; height: number };
  source?: "ai" | "admin" | string;
  description?: string;
}

const getActiveMarkersForAngle = (
  incident?: FleetInspectionDetailItem | null,
  angleIndex?: number
): ActiveMarkerItem[] => {
  if (!incident || !incident.damages || !Array.isArray(incident.damages) || !angleIndex) {
    return [];
  }

  const activeMarkers: ActiveMarkerItem[] = [];

  incident.damages.forEach((dmg: any, idx: number) => {
    const damageNum = idx + 1;
    const imgNums: number[] = Array.isArray(dmg.image_numbers) && dmg.image_numbers.length > 0
      ? dmg.image_numbers.map(Number)
      : Array.isArray(dmg.images)
      ? dmg.images.map(Number)
      : [];

    if (dmg.markers && Array.isArray(dmg.markers) && dmg.markers.length > 0) {
      dmg.markers.forEach((m: any) => {
        let match = false;
        if (imgNums.length > 0) {
          match = imgNums.includes(angleIndex);
        } else {
          match = Number(m.image_number) === angleIndex || !m.image_number;
        }

        if (match && (m.point || m.bounding_box)) {
          activeMarkers.push({
            damageIndex: damageNum,
            damageId: dmg.damage_id || dmg.id || `DMG_${damageNum}`,
            damageType: dmg.damage_type || dmg.type || "Damage",
            severity: dmg.severity || "minor",
            location: dmg.location || "",
            point: m.point,
            boundingBox: m.bounding_box,
            source: dmg.source || "ai",
            description: dmg.description || dmg.driver_notes,
          });
        }
      });
    } else if (dmg.point || dmg.bounding_box) {
      const match = imgNums.length > 0 ? imgNums.includes(angleIndex) : true;
      if (match) {
        activeMarkers.push({
          damageIndex: damageNum,
          damageId: dmg.damage_id || dmg.id || `DMG_${damageNum}`,
          damageType: dmg.damage_type || dmg.type || "Damage",
          severity: dmg.severity || "minor",
          location: dmg.location || "",
          point: dmg.point,
          boundingBox: dmg.bounding_box,
          source: dmg.source || "ai",
          description: dmg.description || dmg.driver_notes,
        });
      }
    }
  });

  return activeMarkers;
};

const computeImageBounds = (
  container: HTMLElement | null,
  img: HTMLImageElement | null
): { left: number; top: number; width: number; height: number } | null => {
  if (!container || !img) return null;
  if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) return null;

  // 1. Primary: if offsetParent is container, offsetLeft/Top/Width/Height give exact local layout coordinates
  if (img.offsetParent === container && img.offsetWidth > 0 && img.offsetHeight > 0) {
    return {
      left: img.offsetLeft,
      top: img.offsetTop,
      width: img.offsetWidth,
      height: img.offsetHeight,
    };
  }

  // 2. Normalized getBoundingClientRect fallback
  const cRect = container.getBoundingClientRect();
  const iRect = img.getBoundingClientRect();
  if (cRect.width > 0 && cRect.height > 0 && iRect.width > 0 && iRect.height > 0) {
    const scaleX = (container.offsetWidth && cRect.width) ? cRect.width / container.offsetWidth : 1;
    const scaleY = (container.offsetHeight && cRect.height) ? cRect.height / container.offsetHeight : 1;
    const safeScaleX = scaleX > 0 ? scaleX : 1;
    const safeScaleY = scaleY > 0 ? scaleY : 1;
    return {
      left: (iRect.left - cRect.left) / safeScaleX,
      top: (iRect.top - cRect.top) / safeScaleY,
      width: iRect.width / safeScaleX,
      height: iRect.height / safeScaleY,
    };
  }

  // 3. Mathematical aspect-ratio fallback
  const cW = container.clientWidth || container.offsetWidth;
  const cH = container.clientHeight || container.offsetHeight;
  const nW = img.naturalWidth;
  const nH = img.naturalHeight;
  if (cW > 0 && cH > 0 && nW > 0 && nH > 0) {
    const cRatio = cW / cH;
    const iRatio = nW / nH;
    let rW = cW;
    let rH = cH;
    let offX = 0;
    let offY = 0;
    if (iRatio > cRatio) {
      rW = cW;
      rH = cW / iRatio;
      offY = (cH - rH) / 2;
    } else {
      rH = cH;
      rW = cH * iRatio;
      offX = (cW - rW) / 2;
    }
    return { left: offX, top: offY, width: rW, height: rH };
  }

  return null;
};

const useImageBounds = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  imgRef: React.RefObject<HTMLImageElement | null>,
  src?: string
) => {
  const [bounds, setBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  const updateBounds = useCallback(() => {
    const b = computeImageBounds(containerRef.current, imgRef.current);
    if (b) {
      setBounds((prev) => {
        if (
          prev &&
          Math.abs(prev.left - b.left) < 0.5 &&
          Math.abs(prev.top - b.top) < 0.5 &&
          Math.abs(prev.width - b.width) < 0.5 &&
          Math.abs(prev.height - b.height) < 0.5
        ) {
          return prev;
        }
        return b;
      });
    }
  }, [containerRef, imgRef]);

  useEffect(() => {
    updateBounds();

    const img = imgRef.current;
    const container = containerRef.current;

    const handleImgLoad = () => {
      updateBounds();
    };

    if (img) {
      if (img.complete && img.naturalWidth > 0) {
        updateBounds();
      } else {
        img.addEventListener("load", handleImgLoad);
      }
    }

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        updateBounds();
      });
      if (container) ro.observe(container);
      if (img) ro.observe(img);
    }

    window.addEventListener("resize", updateBounds);

    const t1 = setTimeout(updateBounds, 50);
    const t2 = setTimeout(updateBounds, 150);
    const t3 = setTimeout(updateBounds, 400);

    return () => {
      if (img) img.removeEventListener("load", handleImgLoad);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateBounds);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [updateBounds, src]);

  return { bounds, updateBounds };
};

const DamageImageOverlay: React.FC<{
  markers: ActiveMarkerItem[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  imgRef: React.RefObject<HTMLImageElement | null>;
  src?: string;
}> = ({ markers, containerRef, imgRef, src }) => {
  const { bounds } = useImageBounds(containerRef, imgRef, src);

  if (!markers || markers.length === 0) return null;
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: `${bounds.left}px`,
        top: `${bounds.top}px`,
        width: `${bounds.width}px`,
        height: `${bounds.height}px`,
        pointerEvents: "none",
        zIndex: 10,
      }}
    >
      {markers.map((m, i) => {
        let boxLeft = 0;
        let boxTop = 0;
        let boxWidth = 14;
        let boxHeight = 10;

        if (m.boundingBox) {
          const rawX = m.boundingBox.x;
          const rawY = m.boundingBox.y;
          const rawW = m.boundingBox.width;
          const rawH = m.boundingBox.height;
          boxLeft = rawX > 1 ? rawX : rawX * 100;
          boxTop = rawY > 1 ? rawY : rawY * 100;
          boxWidth = rawW > 1 ? rawW : rawW * 100;
          boxHeight = rawH > 1 ? rawH : rawH * 100;
        } else if (m.point) {
          const pX = m.point.x > 1 ? m.point.x / 100 : m.point.x;
          const pY = m.point.y > 1 ? m.point.y / 100 : m.point.y;
          boxWidth = 14;
          boxHeight = 10;
          boxLeft = Math.max(0, (pX - 0.07) * 100);
          boxTop = Math.max(0, (pY - 0.05) * 100);
        }

        const isAdmin = m.source === "admin";
        const isCritical = m.severity === "critical" || m.severity === "major";
        const borderColor = isCritical ? "#dc2626" : "#ea580c";
        const bgColor = isCritical ? "rgba(239, 68, 68, 0.16)" : "rgba(249, 115, 22, 0.16)";
        const shadowColor = isCritical ? "rgba(220, 38, 38, 0.4)" : "rgba(234, 88, 12, 0.4)";

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${boxLeft}%`,
              top: `${boxTop}%`,
              width: `${boxWidth}%`,
              height: `${boxHeight}%`,
              border: `2.5px solid ${borderColor}`,
              borderRadius: "6px",
              backgroundColor: bgColor,
              boxShadow: `0 0 0 2px ${bgColor}, 0 4px 12px ${shadowColor}`,
              pointerEvents: "auto",
              cursor: "pointer",
              transition: "all 0.15s ease-in-out",
              zIndex: 12,
            }}
            title={`#${m.damageIndex}: ${m.damageType}${isAdmin ? " (Admin Marked)" : ""} (${m.location})${m.description ? `\nNote: ${m.description}` : ""}`}
          >
            {/* Top-Left Damage Number & Type Badge */}
            <div
              style={{
                position: "absolute",
                top: boxTop < 8 ? "2px" : "-13px",
                left: "-2px",
                backgroundColor: borderColor,
                color: "#ffffff",
                fontSize: "10px",
                fontWeight: 800,
                padding: "2px 6px",
                borderRadius: "4px",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                pointerEvents: "none",
              }}
            >
              #{m.damageIndex} {m.damageType} {isAdmin ? "• ADMIN" : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Normal Static Image Component for Main Page Previous & Current Comparison Cards
 * Clicking opens the large comparison modal (no hover zoom on main page)
 */
const CompareNormalImage: React.FC<{
  src?: string;
  alt: string;
  onClick?: () => void;
  markers?: ActiveMarkerItem[];
  isMarkingMode?: boolean;
  onBoxDrawn?: (box: { x: number; y: number; width: number; height: number }) => void;
}> = ({ src, alt, onClick, markers, isMarkingMode = false, onBoxDrawn }) => {
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentBox, setCurrentBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const { bounds, updateBounds } = useImageBounds(containerRef, imgRef, src);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMarkingMode) return;
    e.preventDefault();
    e.stopPropagation();
    const b = computeImageBounds(containerRef.current, imgRef.current) || bounds;
    if (!b || b.width === 0 || b.height === 0) return;
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    const scaleX = (containerRef.current?.offsetWidth && containerRect.width)
      ? containerRect.width / containerRef.current.offsetWidth
      : 1;
    const scaleY = (containerRef.current?.offsetHeight && containerRect.height)
      ? containerRect.height / containerRef.current.offsetHeight
      : 1;

    const mouseX = (e.clientX - containerRect.left) / (scaleX || 1);
    const mouseY = (e.clientY - containerRect.top) / (scaleY || 1);

    const clickX = mouseX - b.left;
    const clickY = mouseY - b.top;

    const normX = Math.max(0, Math.min(1, clickX / b.width));
    const normY = Math.max(0, Math.min(1, clickY / b.height));

    setIsDrawing(true);
    setStartPoint({ x: normX, y: normY });
    setCurrentBox({ x: normX, y: normY, width: 0, height: 0 });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMarkingMode || !isDrawing || !startPoint) return;
    e.preventDefault();
    const b = computeImageBounds(containerRef.current, imgRef.current) || bounds;
    if (!b || b.width === 0 || b.height === 0) return;
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    const scaleX = (containerRef.current?.offsetWidth && containerRect.width)
      ? containerRect.width / containerRef.current.offsetWidth
      : 1;
    const scaleY = (containerRef.current?.offsetHeight && containerRect.height)
      ? containerRect.height / containerRef.current.offsetHeight
      : 1;

    const mouseX = (e.clientX - containerRect.left) / (scaleX || 1);
    const mouseY = (e.clientY - containerRect.top) / (scaleY || 1);

    const currentX = mouseX - b.left;
    const currentY = mouseY - b.top;

    const normX = Math.max(0, Math.min(1, currentX / b.width));
    const normY = Math.max(0, Math.min(1, currentY / b.height));

    const x = Math.min(startPoint.x, normX);
    const y = Math.min(startPoint.y, normY);
    const width = Math.abs(normX - startPoint.x);
    const height = Math.abs(normY - startPoint.y);

    setCurrentBox({ x, y, width, height });
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMarkingMode || !isDrawing) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDrawing(false);
    if (currentBox && currentBox.width >= 0.015 && currentBox.height >= 0.015) {
      onBoxDrawn?.({
        x: Math.round(currentBox.x * 1000) / 1000,
        y: Math.round(currentBox.y * 1000) / 1000,
        width: Math.round(currentBox.width * 1000) / 1000,
        height: Math.round(currentBox.height * 1000) / 1000,
      });
    }
    setCurrentBox(null);
    setStartPoint(null);
  };

  if (!src || hasError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#64748b",
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={onClick}
      >
        <Camera size={32} strokeWidth={1.5} />
        <span style={{ fontSize: "11px", marginTop: "4px" }}>No Image Available</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onClick={isMarkingMode ? undefined : onClick}
      onMouseDown={isMarkingMode ? handleMouseDown : undefined}
      onMouseMove={isMarkingMode ? handleMouseMove : undefined}
      onMouseUp={isMarkingMode ? handleMouseUp : undefined}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: "8px",
        position: "relative",
        cursor: isMarkingMode ? "crosshair" : "pointer",
        backgroundColor: "#0f172a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        userSelect: isMarkingMode ? "none" : "auto",
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        referrerPolicy="no-referrer"
        onLoad={() => updateBounds()}
        style={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          borderRadius: "8px",
          pointerEvents: isMarkingMode ? "none" : "auto",
        }}
        onError={() => setHasError(true)}
      />
      {markers && markers.length > 0 && (
        <DamageImageOverlay markers={markers} containerRef={containerRef} imgRef={imgRef} src={src} />
      )}

      {/* Live Drawing Box Preview */}
      {isMarkingMode && currentBox && bounds && (
        <div
          style={{
            position: "absolute",
            left: `${bounds.left}px`,
            top: `${bounds.top}px`,
            width: `${bounds.width}px`,
            height: `${bounds.height}px`,
            pointerEvents: "none",
            zIndex: 35,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: `${currentBox.x * 100}%`,
              top: `${currentBox.y * 100}%`,
              width: `${currentBox.width * 100}%`,
              height: `${currentBox.height * 100}%`,
              border: "2.5px solid #ea580c",
              backgroundColor: "rgba(249, 115, 22, 0.22)",
              borderRadius: "4px",
              boxShadow: "0 0 0 2px rgba(249, 115, 22, 0.3), 0 4px 12px rgba(234, 88, 12, 0.4)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-18px",
                left: "-2px",
                backgroundColor: "#ea580c",
                color: "#ffffff",
                fontSize: "10px",
                fontWeight: 800,
                padding: "1px 6px",
                borderRadius: "3px",
                whiteSpace: "nowrap",
                letterSpacing: "0.03em",
              }}
            >
              DRAWING MARKER
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Advanced Interactive Modal Image with Cursor Zoom, Drag Pan, Wheel Zoom & Overlay Controls
 */
const ModalCursorZoomImage: React.FC<{
  src?: string;
  alt: string;
  zoomEnabled?: boolean;
  damageInfo?: { hasDamage: boolean; count?: number; label?: string };
  markers?: ActiveMarkerItem[];
  isMarkingMode?: boolean;
  onBoxDrawn?: (box: { x: number; y: number; width: number; height: number }) => void;
}> = ({ src, alt, zoomEnabled = false, damageInfo, markers, isMarkingMode = false, onBoxDrawn }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [origin, setOrigin] = useState("center center");
  const [fitMode, setFitMode] = useState<"contain" | "cover">("contain");

  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentBox, setCurrentBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const { bounds, updateBounds } = useImageBounds(containerRef, imgRef, src);

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [src]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMarkingMode) {
      if (!isDrawing || !startPoint) return;
      e.preventDefault();
      const b = computeImageBounds(containerRef.current, imgRef.current) || bounds;
      if (!b || b.width === 0 || b.height === 0) return;
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const scaleX = (containerRef.current?.offsetWidth && containerRect.width)
        ? containerRect.width / containerRef.current.offsetWidth
        : 1;
      const scaleY = (containerRef.current?.offsetHeight && containerRect.height)
        ? containerRect.height / containerRef.current.offsetHeight
        : 1;

      const mouseX = (e.clientX - containerRect.left) / (scaleX || 1);
      const mouseY = (e.clientY - containerRect.top) / (scaleY || 1);

      const currentX = mouseX - b.left;
      const currentY = mouseY - b.top;

      const normX = Math.max(0, Math.min(1, currentX / b.width));
      const normY = Math.max(0, Math.min(1, currentY / b.height));

      const x = Math.min(startPoint.x, normX);
      const y = Math.min(startPoint.y, normY);
      const width = Math.abs(normX - startPoint.x);
      const height = Math.abs(normY - startPoint.y);

      setCurrentBox({ x, y, width, height });
      return;
    }

    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        setHasDragged(true);
      }
      setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
      return;
    }

    if (zoomEnabled && scale === 1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setOrigin(`${x.toFixed(1)}% ${y.toFixed(1)}%`);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMarkingMode) {
      e.preventDefault();
      e.stopPropagation();
      const b = computeImageBounds(containerRef.current, imgRef.current) || bounds;
      if (!b || b.width === 0 || b.height === 0) return;
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const scaleX = (containerRef.current?.offsetWidth && containerRect.width)
        ? containerRect.width / containerRef.current.offsetWidth
        : 1;
      const scaleY = (containerRef.current?.offsetHeight && containerRect.height)
        ? containerRect.height / containerRef.current.offsetHeight
        : 1;

      const mouseX = (e.clientX - containerRect.left) / (scaleX || 1);
      const mouseY = (e.clientY - containerRect.top) / (scaleY || 1);

      const clickX = mouseX - b.left;
      const clickY = mouseY - b.top;

      const normX = Math.max(0, Math.min(1, clickX / b.width));
      const normY = Math.max(0, Math.min(1, clickY / b.height));

      setIsDrawing(true);
      setStartPoint({ x: normX, y: normY });
      setCurrentBox({ x: normX, y: normY, width: 0, height: 0 });
      return;
    }

    setHasDragged(false);
    if (scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMarkingMode) {
      e.preventDefault();
      e.stopPropagation();
      setIsDrawing(false);
      if (currentBox && currentBox.width >= 0.015 && currentBox.height >= 0.015) {
        onBoxDrawn?.({
          x: Math.round(currentBox.x * 1000) / 1000,
          y: Math.round(currentBox.y * 1000) / 1000,
          width: Math.round(currentBox.width * 1000) / 1000,
          height: Math.round(currentBox.height * 1000) / 1000,
        });
      }
      setCurrentBox(null);
      setStartPoint(null);
      return;
    }
    setIsDragging(false);
  };

  const handleImageClick = () => {
    if (isMarkingMode) return;
    if (src && !hasDragged) {
      window.open(src, "_blank");
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (isMarkingMode) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.25 : -0.25;
    setScale((prev) => {
      const next = Math.min(Math.max(1, prev + delta), 5);
      if (next === 1) setPosition({ x: 0, y: 0 });
      return parseFloat(next.toFixed(2));
    });
  };

  const zoomIn = () => setScale((prev) => Math.min(5, parseFloat((prev + 0.5).toFixed(2))));
  const zoomOut = () =>
    setScale((prev) => {
      const next = Math.max(1, parseFloat((prev - 0.5).toFixed(2)));
      if (next === 1) setPosition({ x: 0, y: 0 });
      return next;
    });

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  if (!src) {
    return (
      <div style={{ color: "#94a3b8", fontSize: "13px", display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
        <Camera size={32} color="#64748b" />
        <span>No Inspection Photo Available</span>
      </div>
    );
  }

  return (
    <div
      onClick={handleImageClick}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        setIsDragging(false);
        setIsHovered(false);
        if (scale === 1) setOrigin("center center");
      }}
      onMouseEnter={() => setIsHovered(true)}
      onWheel={handleWheel}
      title={isMarkingMode ? "Click & drag to mark missed damage" : "Click to open full image in a new tab"}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: "10px",
        position: "relative",
        cursor: isMarkingMode ? "crosshair" : scale > 1 ? (isDragging ? "grabbing" : "grab") : "pointer",
        backgroundColor: "#090d16",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #1e293b",
        userSelect: isMarkingMode ? "none" : "auto",
      }}
    >
      {/* Scale Badge Overlay */}
      {!isMarkingMode && scale > 1 && (
        <div
          style={{
            position: "absolute",
            top: "12px",
            left: "12px",
            zIndex: 10,
            backgroundColor: "rgba(15, 23, 42, 0.85)",
            color: "#ffcb0b",
            padding: "4px 10px",
            borderRadius: "6px",
            fontSize: "11.5px",
            fontWeight: 700,
            border: "1px solid rgba(255, 203, 11, 0.4)",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Move size={12} />
          <span>{Math.round(scale * 100)}% Zoom (Drag to pan)</span>
        </div>
      )}

      {/* Main Transform Container for Image + Markers */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          transformOrigin: scale === 1 ? origin : "center center",
          transform: !isMarkingMode && scale > 1 ? `translate(${position.x}px, ${position.y}px) scale(${scale})` : !isMarkingMode && zoomEnabled && isHovered ? "scale(2.8)" : "scale(1)",
          transition: isDragging ? "none" : isHovered || scale > 1 ? "transform 0.05s ease-out" : "transform 0.2s ease-out",
        }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          referrerPolicy="no-referrer"
          onLoad={() => updateBounds()}
          style={{
            maxWidth: fitMode === "contain" ? "100%" : "none",
            maxHeight: fitMode === "contain" ? "100%" : "none",
            width: fitMode === "cover" ? "100%" : "auto",
            height: fitMode === "cover" ? "100%" : "auto",
            objectFit: fitMode,
            borderRadius: "6px",
            userSelect: "none",
            pointerEvents: "none",
            display: "block",
          }}
        />
        {markers && markers.length > 0 && (
          <DamageImageOverlay markers={markers} containerRef={containerRef} imgRef={imgRef} src={src} />
        )}

        {/* Live Drawing Box Preview in Modal */}
        {isMarkingMode && currentBox && bounds && (
          <div
            style={{
              position: "absolute",
              left: `${bounds.left}px`,
              top: `${bounds.top}px`,
              width: `${bounds.width}px`,
              height: `${bounds.height}px`,
              pointerEvents: "none",
              zIndex: 35,
            }}
          >
            <div
              style={{
                position: "absolute",
                left: `${currentBox.x * 100}%`,
                top: `${currentBox.y * 100}%`,
                width: `${currentBox.width * 100}%`,
                height: `${currentBox.height * 100}%`,
                border: "2.5px solid #ea580c",
                backgroundColor: "rgba(249, 115, 22, 0.22)",
                borderRadius: "4px",
                boxShadow: "0 0 0 2px rgba(249, 115, 22, 0.3), 0 4px 12px rgba(234, 88, 12, 0.4)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-18px",
                  left: "-2px",
                  backgroundColor: "#ea580c",
                  color: "#ffffff",
                  fontSize: "10px",
                  fontWeight: 800,
                  padding: "1px 6px",
                  borderRadius: "3px",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.03em",
                }}
              >
                DRAWING MARKER
              </div>
            </div>
          </div>
        )}
      </div>

      {/* On-Image Quick Zoom Control Toolbar */}
      {!isMarkingMode && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            bottom: "12px",
            right: "12px",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            backgroundColor: "rgba(15, 23, 42, 0.85)",
            padding: "4px 8px",
            borderRadius: "8px",
            border: "1px solid #334155",
            backdropFilter: "blur(4px)",
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); zoomOut(); }}
            title="Zoom Out (-)"
            style={{
              background: "transparent",
              border: "none",
              color: "#cbd5e1",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ZoomOut size={15} />
          </button>
          <span style={{ color: "#ffcb0b", fontSize: "11px", fontWeight: 700, minWidth: "36px", textAlign: "center" }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); zoomIn(); }}
            title="Zoom In (+)"
            style={{
              background: "transparent",
              border: "none",
              color: "#cbd5e1",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ZoomIn size={15} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); resetZoom(); }}
            title="Reset Zoom"
            style={{
              background: "transparent",
              border: "none",
              color: "#cbd5e1",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              marginLeft: "2px",
            }}
          >
            <RotateCcw size={13} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFitMode(fitMode === "contain" ? "cover" : "contain"); }}
            title={fitMode === "contain" ? "Fit Container (Cover)" : "Fit Image (Contain)"}
            style={{
              background: fitMode === "cover" ? "#ffcb0b" : "transparent",
              color: fitMode === "cover" ? "#0f172a" : "#cbd5e1",
              border: "none",
              fontSize: "10.5px",
              fontWeight: 700,
              cursor: "pointer",
              padding: "2px 6px",
              borderRadius: "4px",
              marginLeft: "4px",
            }}
          >
            {fitMode === "contain" ? "FIT" : "FILL"}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); window.open(src, "_blank"); }}
            title="Open full image in a new tab"
            style={{
              background: "#2563eb",
              color: "#ffffff",
              border: "none",
              fontSize: "10.5px",
              fontWeight: 700,
              cursor: "pointer",
              padding: "3px 7px",
              borderRadius: "4px",
              marginLeft: "4px",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <ExternalLink size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

interface DamageAnalysisProps {
  inspectionId?: string;
  onBack?: () => void;
}

const DamageAnalysis: React.FC<DamageAnalysisProps> = ({ inspectionId, onBack }) => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [detailData, setDetailData] = useState<FleetInspectionDetailResponse | null>(null);
  const [selectedAngle, setSelectedAngle] = useState<string>("Left Side");
  const [override, setOverride] = useState<string>("confirmed");
  const [notes, setNotes] = useState<string>("");

  const [saving, setSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Defect Action Buttons state (5 buttons per defect)
  const [defectActions, setDefectActions] = useState<Record<number, string>>({});
  const [repairDates, setRepairDates] = useState<Record<number, string>>({});
  const [showRulesGuide, setShowRulesGuide] = useState<boolean>(false);
  // Admin Manual Damage Marking States
  const [isMarkingMode, setIsMarkingMode] = useState<boolean>(false);
  const [drawnBox, setDrawnBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [isAddDamageModalOpen, setIsAddDamageModalOpen] = useState<boolean>(false);
  const [newDamageType, setNewDamageType] = useState<string>("SCUFF");
  const [newDamageDescription, setNewDamageDescription] = useState<string>("");
  const [newDamageSeverity, setNewDamageSeverity] = useState<string>("minor");
  const [isSubmittingDamage, setIsSubmittingDamage] = useState<boolean>(false);

  const handleBoxDrawn = (box: { x: number; y: number; width: number; height: number }) => {
    setDrawnBox(box);
    setIsAddDamageModalOpen(true);
  };

  const handleSaveAdminDamage = async () => {
    if (!drawnBox || !currentIncident) return;
    setIsSubmittingDamage(true);
    try {
      const activeInspId = currentIncident.id || (currentIncident as any).inspection_id || inspectionId || id || 17;
      const vehicleNum = currentIncident.vehicle_number || (currentIncident as any).van_name || "NU74 YBF";
      const payload = {
        inspection_id: activeInspId,
        vehicle_number: vehicleNum,
        image_number: [selectedConfig.index],
        damage_type: newDamageType,
        description: newDamageDescription || `${newDamageType} marked manually on ${selectedConfig.label}`,
        severity: newDamageSeverity,
        bounding_box: drawnBox,
      };

      const res = await DashboardApi.addAdminInspectionDamage(payload);
      const created = res.data || {
        damage_id: Date.now(),
        ...payload,
        confidence: 100,
        source: "admin",
        status: "DETECTED_CURRENTLY",
        image_numbers: [selectedConfig.index],
        markers: [{ image_number: selectedConfig.index, bounding_box: drawnBox }],
        vehicle_number: vehicleNum,
      };

      setDetailData((prev) => {
        if (!prev || !prev.incident_data) return prev;
        const currentDamages = Array.isArray(prev.incident_data.damages) ? [...prev.incident_data.damages] : [];
        return {
          ...prev,
          incident_data: {
            ...prev.incident_data,
            damage_points: (prev.incident_data.damage_points || 0) + 1,
            damages: [...currentDamages, created],
          },
        };
      });

      setIsAddDamageModalOpen(false);
      setIsMarkingMode(false);
      setDrawnBox(null);
      setNewDamageDescription("");
      setSaveMessage("✓ Damage marked and added to defect breakdown successfully!");
      setTimeout(() => setSaveMessage(null), 4000);
    } catch (err) {
      console.error("Failed to save admin damage:", err);
    } finally {
      setIsSubmittingDamage(false);
    }
  };

  // const handleDeleteAdminDamage = async (damageId: string | number, idx: number) => {
  //   if (!window.confirm("Are you sure you want to remove this admin damage marker?")) return;
  //   const activeInspId = currentIncident?.id || (currentIncident as any)?.inspection_id || inspectionId || id || 17;
  //   try {
  //     await DashboardApi.deleteAdminInspectionDamage({
  //       inspection_id: activeInspId,
  //       damage_id: damageId,
  //     });

  //     setDetailData((prev) => {
  //       if (!prev || !prev.incident_data) return prev;
  //       const updated = (prev.incident_data.damages || []).filter((_, i) => i !== idx);
  //       return {
  //         ...prev,
  //         incident_data: {
  //           ...prev.incident_data,
  //           damage_points: Math.max(0, (prev.incident_data.damage_points || 0) - 1),
  //           damages: updated,
  //         },
  //       };
  //     });
  //     setSaveMessage("✓ Admin damage marker removed.");
  //     setTimeout(() => setSaveMessage(null), 3000);
  //   } catch (err) {
  //     console.error("Failed to delete admin damage:", err);
  //   }
  // };

  const currentIncident = detailData?.incident_data;
  const oldIncident = detailData?.old_incident_data;

  const getStatusBadgeConfig = (action: string, defaultStat: string) => {
    switch (action) {
      case "resolved_after_repair":
      case "resolved":
        return {
          label: "CLOSED",
          bgColor: "#dcfce7",
          color: "#15803d",
          borderColor: "#86efac",
          subtext: "Damage fixed and closed in system (will not appear in future inspections)",
          IconComp: CheckCircle2,
        };
      case "booked_in_for_repair":
      case "booked":
        return {
          label: "BOOKED IN",
          bgColor: "#dbeafe",
          color: "#1d4ed8",
          borderColor: "#bfdbfe",
          subtext: "Set for repair (will show as Existing on next inspection)",
          IconComp: Calendar,
        };
      case "ignore_once":
        return {
          label: "IGNORED ONCE",
          bgColor: "#f1f5f9",
          color: "#475569",
          borderColor: "#cbd5e1",
          subtext: "Ignored for today only (will reappear tomorrow if still present)",
          IconComp: EyeOff,
        };
      case "ignore_permanently":
      case "ignore_perm":
        return {
          label: "IGNORED PERMANENTLY",
          bgColor: "#1e293b",
          color: "#ffffff",
          borderColor: "#0f172a",
          subtext: "Ignored for all future inspections (will not show again)",
          IconComp: Ban,
        };
      case "confirmed_damage":
      case "confirmed":
        return {
          label: "CONFIRMED DAMAGE",
          bgColor: "#fee2e2",
          color: "#dc2626",
          borderColor: "#fca5a5",
          subtext: "Verified damage open until resolved",
          IconComp: AlertTriangle,
        };
      default:
        return {
          label: `Status: ${defaultStat}`,
          bgColor: "#f1f5f9",
          color: "#475569",
          borderColor: "#cbd5e1",
          subtext: null,
          IconComp: Info,
        };
    }
  };

  // Large Side-by-Side Comparison Box Modal State & Zoom Toggle State
  const [isCompareModalOpen, setIsCompareModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"compare" | "previous" | "current" | "summary">("compare");
  const [isZoomEnabled, setIsZoomEnabled] = useState<boolean>(false);
  const [isModalFullScreen, setIsModalFullScreen] = useState<boolean>(true);

  const openCompareModal = (mode: "compare" | "previous" | "current" | "summary") => {
    setModalMode(mode);
    setIsCompareModalOpen(true);
  };

  useEffect(() => {
    const targetId = inspectionId || id || sessionStorage.getItem("last_viewed_inspection_id") || "17";
    fetchDetail(targetId);
  }, [inspectionId, id]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsCompareModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const getTodayString = () => new Date().toISOString().split("T")[0];

  const fetchDetail = async (targetInspectionId: string) => {
    setLoading(true);
    try {
      const data = await DashboardApi.getFleetInspectionDetail(targetInspectionId);
      setDetailData(data);

      if (data?.incident_data) {
        const item = data.incident_data as any;
        const noteVal = item.admin_notes || item.admin_note || item.remarks || item.comment || item.manager_notes;
        const statusVal = item.admin_status || item.status;

        if (noteVal !== undefined && noteVal !== null) {
          setNotes(String(noteVal));
        }
        if (statusVal && String(statusVal).trim()) {
          setOverride(String(statusVal));
        }

        const loadedActions: Record<number, string> = {};
        const loadedDates: Record<number, string> = {};

        if (Array.isArray(item.damages)) {
          item.damages.forEach((dmg: any, idx: number) => {
            const st = dmg.action_status || dmg.status;
            if (st && ["resolved_after_repair", "booked_in_for_repair", "ignore_once", "ignore_permanently", "confirmed_damage"].includes(st)) {
              loadedActions[idx] = st;
            }
            if (dmg.repair_date) {
              loadedDates[idx] = dmg.repair_date;
            }
          });
        }
        setDefectActions(loadedActions);
        setRepairDates(loadedDates);
      }
    } catch (err) {
      console.error("Error fetching inspection detail:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReview = async () => {
    const targetId = currentIncident?.id || (currentIncident as any)?.inspection_id || inspectionId || id || "17";
    if (!targetId) return;

    setSaving(true);
    setSaveMessage(null);
    try {
      await DashboardApi.addAdminFleetInspectionNotes({
        inspection_id: targetId,
        admin_notes: notes,
        admin_status: override,
      });

      if (detailData?.incident_data) {
        setDetailData({
          ...detailData,
          incident_data: {
            ...detailData.incident_data,
            admin_notes: notes,
            admin_status: override,
          },
        });
      }

      setSaveMessage("✅ Admin notes updated and saved successfully!");
      setTimeout(() => setSaveMessage(null), 4000);
    } catch (err) {
      console.error("Error saving admin notes:", err);
      alert("Failed to save admin notes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDefectActionChange = async (
    defectIdx: number,
    dmg: any,
    actionStatus: "resolved_after_repair" | "booked_in_for_repair" | "ignore_once" | "ignore_permanently" | "confirmed_damage",
    repairDateStr?: string
  ) => {
    const activeInspId = currentIncident?.id || (currentIncident as any)?.inspection_id || inspectionId || id || "17";

    const newDefectActions = { ...defectActions, [defectIdx]: actionStatus };
    setDefectActions(newDefectActions);

    if (repairDateStr) {
      const newRepairDates = { ...repairDates, [defectIdx]: repairDateStr };
      setRepairDates(newRepairDates);
    }

    const damageId = dmg?.id || dmg?.damage_id || defectIdx + 1;

    try {
      await DashboardApi.changeVanInspectionDamageStatus({
        inspaction_id: activeInspId,
        action_status: actionStatus,
        damage_id: damageId,
      });
    } catch (err) {
      console.error("Error updating inspection damage status:", err);
    }
  };

  const getDamageForAngle = (angleLabel: string, incidentData?: FleetInspectionDetailItem | null): DamageItem | undefined => {
    if (!incidentData || !incidentData.damages || !Array.isArray(incidentData.damages)) return undefined;
    const config = ANGLE_CONFIG.find((a) => a.label === angleLabel);
    if (!config) return undefined;

    return incidentData.damages.find((d: any) => {
      if (d.markers && Array.isArray(d.markers) && d.markers.some((m: any) => m.image_number === config.index)) {
        return true;
      }
      if (d.image_numbers && Array.isArray(d.image_numbers) && d.image_numbers.includes(config.index)) {
        return true;
      }
      if (d.location && d.location.toLowerCase().includes(angleLabel.toLowerCase().replace("-", " "))) {
        return true;
      }
      return false;
    });
  };

  const selectedConfig = ANGLE_CONFIG.find((a) => a.label === selectedAngle) || ANGLE_CONFIG[2];

  const currentImg = currentIncident ? getAngleImage(currentIncident, selectedConfig.key) : undefined;
  const oldImg = oldIncident ? getAngleImage(oldIncident, selectedConfig.key) : undefined;

  const currentDamage = getDamageForAngle(selectedAngle, currentIncident);
  const oldDamage = getDamageForAngle(selectedAngle, oldIncident);

  const currentAngleMarkers = getActiveMarkersForAngle(currentIncident, selectedConfig.index);
  const oldAngleMarkers = getActiveMarkersForAngle(oldIncident, selectedConfig.index);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Back button */}
        <div style={{ marginBottom: "18px" }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const activeId = currentIncident?.id || id;
              if (activeId) {
                sessionStorage.setItem("last_viewed_inspection_id", String(activeId));
              }
              if (onBack) {
                onBack();
              } else {
                history.push("/fleet", { highlightedInspectionId: activeId });
              }
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              border: "1px solid #cbd5e1",
              fontSize: "13px",
              fontWeight: 600,
              color: "#334155",
              cursor: "pointer",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              transition: "all 0.2s ease",
            }}
          >
            <ArrowLeft size={16} />
            Back to Fleet Inspections
          </button>
        </div>

        {/* Header */}
        <header className={styles.header}>
          <h1 className={styles.title}>
            Inspection Detail &amp; Damage Analysis {currentIncident?.id ? `(#${currentIncident.id})` : id ? `(#${id})` : ""}
          </h1>
          <p className={styles.subtitle}>
            10-angle inspection viewer with AI damage classification, side-by-side comparison, PDF export.
          </p>
        </header>

        {loading ? (
          <div className={styles.card} style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
            Loading vehicle inspection detail...
          </div>
        ) : !currentIncident ? (
          <div className={styles.card} style={{ textAlign: "center", padding: "40px", color: "#ef4444" }}>
            <AlertCircle size={32} style={{ marginBottom: "12px" }} />
            <p style={{ fontWeight: 600, fontSize: "16px" }}>Inspection record #{id} could not be loaded.</p>
            <button
              type="button"
              onClick={() => fetchDetail(id || "17")}
              style={{
                marginTop: "16px",
                padding: "8px 18px",
                backgroundColor: "#3b82f6",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Summary Information Banner Card */}
            <section className={styles.card} style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
                <div>
                  <h2 className={styles.cardTitle} style={{ fontSize: "20px", fontWeight: 700, color: "#1f1a17" }}>
                    {currentIncident.vehicle_number || "N/A"}
                  </h2>
                  <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#64748b", fontWeight: 500 }}>
                    Workforce Name: <strong style={{ color: "#1f1a17" }}>{currentIncident.driver_name || "N/A"}</strong>
                  </p>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                  {oldIncident && (
                    <div style={{ textAlign: "right", borderRight: "1px solid #e2e8f0", paddingRight: "16px" }}>
                      <span style={{ display: "block", fontSize: "10.5px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                        Previous Inspection (#{oldIncident.id})
                      </span>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>
                        {formatDateDDMMYYYY(oldIncident.inspection_date)} ({oldIncident.damage_points ?? 0} pts)
                      </span>
                    </div>
                  )}

                  <div style={{ textAlign: "right" }}>
                    <span style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                      Inspection Date
                    </span>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: "#1f1a17" }}>
                      {formatDateDDMMYYYY(currentIncident.inspection_date)}
                    </span>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <span style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                      Damage Points
                    </span>
                    <span
                      style={{
                        display: "inline-block",
                        backgroundColor: (currentIncident.damage_points ?? 0) > 0 ? "#fee2e2" : "#d1fae5",
                        color: (currentIncident.damage_points ?? 0) > 0 ? "#991b1b" : "#065f46",
                        padding: "3px 10px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: 700,
                        marginTop: "2px",
                      }}
                    >
                      {currentIncident.damage_points ?? 0} pts
                    </span>
                  </div>

                  <span
                    style={{
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      backgroundColor: ((currentIncident.status || currentIncident.inspection_status || "").toLowerCase().includes("damage") && !(currentIncident.status || "").toLowerCase().includes("no_damage")) ? "#fee2e2" : "#d1fae5",
                      color: ((currentIncident.status || currentIncident.inspection_status || "").toLowerCase().includes("damage") && !(currentIncident.status || "").toLowerCase().includes("no_damage")) ? "#991b1b" : "#065f46",
                    }}
                  >
                    {(currentIncident.status || currentIncident.inspection_status || "CLEAR").toUpperCase()}
                  </span>
                </div>
              </div>
            </section>

            {/* 8-Angle Inspection Card */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>
                10-Angle Inspection Photos
              </h2>
              <p className={styles.cardHint}>Click any angle to select view below</p>

              <div className={styles.angleGrid}>
                {ANGLE_CONFIG.map((angle) => {
                  const angleImg = getAngleImage(currentIncident, angle.key);
                  const angleDamage = getDamageForAngle(angle.label, currentIncident);
                  const isDamage = !!angleDamage;

                  return (
                    <button
                      key={angle.label}
                      type="button"
                      onClick={() => setSelectedAngle(angle.label)}
                      className={`${styles.angleTile} ${
                        selectedAngle === angle.label ? styles.angleTileActive : ""
                      }`}
                    >
                      <span
                        className={`${styles.statusDot} ${
                          isDamage ? styles.statusDamage : styles.statusOk
                        }`}
                      />
                      <div className={styles.angleThumb}>
                        <AngleThumbImage src={angleImg} alt={angle.label} />
                      </div>
                      <p className={styles.angleLabel}>{angle.label}</p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Inspection Photo Comparison Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "24px",
                marginBottom: "12px",
                padding: "14px 20px",
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div>
                <h3 style={{ fontSize: "15.5px", fontWeight: 700, color: "#1f1a17", margin: 0 }}>
                  Inspection Photo Comparison ({selectedAngle})
                </h3>
                <p style={{ fontSize: "12.5px", color: "#64748b", margin: "2px 0 0 0" }}>
                  Click photos or "Expand Side-by-Side Box" to open full view in modal canvas.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsCompareModalOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  backgroundColor: "#ffcb0b",
                  color: "#1f1a17",
                  border: "none",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(255, 203, 11, 0.35)",
                }}
              >
                <Maximize2 size={15} />
                Expand Side-by-Side Box
              </button>
            </div>

            {/* Previous / Current comparison cards (ONLY HERE) */}
            <section className={styles.compareGrid}>
              {/* Previous Inspection Card */}
              <div className={styles.card}>
                <div>
                  <h3 className={styles.cardTitle}>
                    Previous Inspection {oldIncident ? `(${oldIncident.vehicle_number || (oldIncident as any).van_name || currentIncident.vehicle_number || "Same Van"})` : ""}
                  </h3>
                  <p className={styles.compareDate}>
                    {oldIncident?.inspection_date ? `${formatDateDDMMYYYY(oldIncident.inspection_date)} — ${selectedAngle}` : `Initial Inspection — ${selectedAngle}`}
                  </p>
                </div>

                <div className={styles.compareImage}>
                  {oldIncident ? (
                    <CompareNormalImage
                      src={oldImg}
                      alt="Previous Inspection"
                      onClick={() => setIsCompareModalOpen(true)}
                      markers={oldAngleMarkers}
                    />
                  ) : (
                    <div
                      onClick={() => setIsCompareModalOpen(true)}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: "#64748b",
                        padding: "20px",
                        textAlign: "center",
                        backgroundColor: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px dashed #cbd5e1",
                        cursor: "pointer",
                      }}
                    >
                      <CheckCircle2 size={32} color="#10b981" style={{ marginBottom: "8px" }} />
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                        Initial Inspection Record
                      </span>
                      <span style={{ fontSize: "11px", marginTop: "4px", color: "#64748b" }}>
                        No previous inspection history recorded for vehicle <strong>{currentIncident.vehicle_number || "N/A"}</strong>.
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Inspection Card */}
              <div className={styles.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "10px", marginBottom: "8px" }}>
                  <div>
                    <h3 className={styles.cardTitle}>
                      Current Inspection ({currentIncident.vehicle_number || "Same Van"})
                    </h3>
                    <p className={styles.compareDate} style={{ margin: "4px 0 0 0" }}>
                      {formatDateDDMMYYYY(currentIncident.inspection_date)} — {selectedAngle} (#{selectedConfig.index})
                    </p>
                  </div>

                  {/* Admin Manual Mark Damage Button */}
                  <button
                    type="button"
                    onClick={() => setIsMarkingMode(!isMarkingMode)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "7px 14px",
                      borderRadius: "20px",
                      backgroundColor: isMarkingMode ? "#dc2626" : "#ffffff",
                      color: isMarkingMode ? "#ffffff" : "#dc2626",
                      border: "1.5px solid #dc2626",
                      fontSize: "12px",
                      fontWeight: 800,
                      cursor: "pointer",
                      boxShadow: isMarkingMode ? "0 0 0 3px rgba(220, 38, 38, 0.25)" : "0 2px 4px rgba(0,0,0,0.04)",
                      transition: "all 0.15s ease",
                    }}
                    title={isMarkingMode ? "Cancel marking mode" : "Click to mark a missed damage on this frame"}
                  >
                    {isMarkingMode ? (
                      <>
                        <X size={15} />
                        <span>Cancel Marking</span>
                      </>
                    ) : (
                      <>
                        <PlusCircle size={15} />
                        <span>+ Mark Damage (Manual)</span>
                      </>
                    )}
                  </button>
                </div>

                {isMarkingMode && (
                  <div
                    style={{
                      marginBottom: "10px",
                      padding: "8px 14px",
                      borderRadius: "8px",
                      backgroundColor: "#fef2f2",
                      border: "1.5px dashed #f87171",
                      color: "#991b1b",
                      fontSize: "12.5px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      boxShadow: "0 2px 6px rgba(220, 38, 38, 0.08)",
                    }}
                  >
                    <MousePointer size={16} color="#dc2626" />
                    <span>
                      <strong>Marking Mode Active:</strong> Click and drag on the car photo below to draw a <strong>Red Box</strong> around the missed damage.
                    </span>
                  </div>
                )}

                <div className={`${styles.compareImage} ${currentDamage ? styles.compareImageDamage : ""}`}>
                  <CompareNormalImage
                    src={currentImg}
                    alt="Current Inspection"
                    onClick={isMarkingMode ? undefined : () => setIsCompareModalOpen(true)}
                    markers={currentAngleMarkers}
                    isMarkingMode={isMarkingMode}
                    onBoxDrawn={handleBoxDrawn}
                  />
                </div>
              </div>
            </section>

            {/* AI Classified Damages & Defect Breakdown Card (Placed Below Photo Comparison) */}
            <section className={styles.card} style={{ marginTop: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
                <div>
                  <h2 className={styles.cardTitle} style={{ fontSize: "17px", fontWeight: 800, margin: 0, display: "flex", alignItems: "center", gap: "10px", color: "#0f172a" }}>
                    <div style={{ width: "26px", height: "26px", borderRadius: "50%", backgroundColor: "#fef3c7", border: "1px solid #fde68a", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <CheckCircle2 size={16} color="#d97706" />
                    </div>
                    <span>AI &amp; Admin Classified Damages &amp; Defect Breakdown ({currentIncident.damages?.length || 0})</span>
                  </h2>
                  <p style={{ margin: "4px 0 0 0", fontSize: "12.5px", color: "#64748b", fontWeight: 500 }}>
                    Review each identified damage or defect and select the appropriate action.
                  </p>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  {/* <button
                    type="button"
                    onClick={() => setShowRulesGuide(!showRulesGuide)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 14px",
                      borderRadius: "20px",
                      backgroundColor: showRulesGuide ? "#1e293b" : "#ffffff",
                      color: showRulesGuide ? "#ffffff" : "#1d4ed8",
                      border: "1.5px solid #3b82f6",
                      fontSize: "12px",
                      fontWeight: 700,
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(59, 130, 246, 0.12)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Info size={14} />
                    <span>{showRulesGuide ? "Hide Action Rules Guide" : "📖 View Action Rules Guide"}</span>
                  </button> */}

                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 14px",
                      borderRadius: "20px",
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fde68a",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#92400e",
                      boxShadow: "0 2px 6px rgba(245, 158, 11, 0.12)",
                    }}
                  >
                    <Layers size={14} color="#d97706" />
                    <span>Total Damage Points: <strong>{currentIncident.damage_points ?? 0} pts</strong></span>
                  </div>
                </div>
              </div>

              {/* Action Rules Guide Banner (Commented Out) */}
              {/* {showRulesGuide && (
                <div
                  style={{
                    marginBottom: "24px",
                    padding: "18px",
                    borderRadius: "14px",
                    backgroundColor: "#f8fafc",
                    border: "1.5px solid #cbd5e1",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                    <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                      <Sparkles size={18} color="#2563eb" />
                      <span>Van Inspection – Damage Action Logic &amp; Rules</span>
                    </h3>
                    <span style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 600 }}>System behavior across daily inspections</span>
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", textAlign: "left" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#1e293b", color: "#ffffff", borderBottom: "2px solid #0f172a" }}>
                          <th style={{ padding: "10px 12px", borderRadius: "6px 0 0 0" }}>Action</th>
                          <th style={{ padding: "10px 12px" }}>What happens today</th>
                          <th style={{ padding: "10px 12px" }}>What happens on next day's inspection</th>
                          <th style={{ padding: "10px 12px" }}>Ongoing behaviour</th>
                          <th style={{ padding: "10px 12px", borderRadius: "0 6px 0 0" }}>System status / notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ backgroundColor: "#f0fdf4", borderBottom: "1px solid #dcfce7" }}>
                          <td style={{ padding: "10px 12px", fontWeight: 700, color: "#166534" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <CheckCircle2 size={16} color="#166534" />
                              <span>Resolved after repair</span>
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Marked as resolved<br />• Damage is closed in system<br />• Closed in today's inspection
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Will not appear in future inspections<br />• Vehicle is shown as clear
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Remains closed unless reopened manually<br />• Reported in history log
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #86efac", padding: "3px 8px", borderRadius: "10px", fontWeight: 800, fontSize: "10.5px" }}>CLOSED</span>
                          </td>
                        </tr>

                        <tr style={{ backgroundColor: "#eff6ff", borderBottom: "1px solid #dbeafe" }}>
                          <td style={{ padding: "10px 12px", fontWeight: 700, color: "#1e40af" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <Calendar size={16} color="#1e40af" />
                              <span>Booked in for repair</span>
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Marked as booked in<br />• Repair date recorded<br />• Kept as open (existing)
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Will appear as 'Existing' with repair date<br />• Not marked as new damage
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Stays on system until repair completed &amp; resolved<br />• Visible in subsequent inspections as 'Existing'
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ backgroundColor: "#dbeafe", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: "10px", fontWeight: 800, fontSize: "10.5px" }}>BOOKED IN</span>
                          </td>
                        </tr>

                        <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <EyeOff size={16} color="#475569" />
                              <span>Ignore Once</span>
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Marked as ignored for today<br />• Will not show in today's inspection
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Will reappear as new damage (if still present)<br />• Can choose another action tomorrow
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • No memory kept<br />• Treated as new damage on next day
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", padding: "3px 8px", borderRadius: "10px", fontWeight: 800, fontSize: "10.5px" }}>IGNORE ONCE</span>
                          </td>
                        </tr>

                        <tr style={{ backgroundColor: "#f1f5f9", borderBottom: "1px solid #cbd5e1" }}>
                          <td style={{ padding: "10px 12px", fontWeight 700, color: "#1e293b" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <Ban size={16} color="#1e293b" />
                              <span>Ignore Permanently</span>
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Marked as ignored permanently<br />• Will not show in today's inspection
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Will not appear in any future inspections<br />• System remembers ignore permanently setting
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Stays ignored until manually changed<br />• Does not appear in reports or daily inspections
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ backgroundColor: "#1e293b", color: "#ffffff", padding: "3px 8px", borderRadius: "10px", fontWeight: 800, fontSize: "10.5px" }}>IGNORED PERMANENTLY</span>
                          </td>
                        </tr>

                        <tr style={{ backgroundColor: "#fef2f2" }}>
                          <td style={{ padding: "10px 12px", fontWeight 700, color: "#991b1b" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <AlertTriangle size={16} color="#991b1b" />
                              <span>Confirmed Damage</span>
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Marked as confirmed damage<br />• Kept open in system<br />• Shows in today's inspection
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Will appear as 'Existing' (not new)<br />• Still visible until resolved or ignored
                          </td>
                          <td style={{ padding: "10px 12px", color: "#334155" }}>
                            • Remains open across all future inspections<br />• Can be updated to booked in, resolved or ignored
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ backgroundColor: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", padding: "3px 8px", borderRadius: "10px", fontWeight: 800, fontSize: "10.5px" }}>CONFIRMED DAMAGE</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )} */}

              {!currentIncident.damages || currentIncident.damages.length === 0 ? (
                <div style={{ padding: "24px 20px", textAlign: "center", color: "#166534", backgroundColor: "#f0fdf4", borderRadius: "12px", border: "1px solid #bbf7d0", fontWeight: 700, fontSize: "14px" }}>
                  ✓ No AI damages detected for this vehicle inspection.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {currentIncident.damages.map((dmg: any, idx: number) => {
                    const isAdmin = dmg.source === "admin" || !!dmg.driver_notes || String(dmg.damage_id || "").startsWith("ADMIN_");
                    const dmgType = dmg.damage_type || dmg.type || "DAMAGE";
                    const dmgLoc = dmg.location || "Vehicle Body Panel";
                    const dmgSev = (dmg.severity || "minor").toLowerCase();
                    const dmgConf = dmg.confidence ? `${dmg.confidence}%` : null;
                    const dmgStat = dmg.status ? String(dmg.status).toUpperCase() : "NEW";
                    const dmgDesc = dmg.description || dmg.driver_notes || "Damage detected during 8-angle inspection scan.";
                    const imgNums = Array.isArray(dmg.image_numbers) ? dmg.image_numbers : [];
                    const activeAction = defectActions[idx] || "";
                    const statusCfg = getStatusBadgeConfig(activeAction, dmgStat);

                    return (
                      <div
                        key={idx}
                        style={{
                          padding: "20px",
                          borderRadius: "14px",
                          backgroundColor: "#ffffff",
                          border: "1.5px solid #e2e8f0",
                          borderLeft: `5px solid ${dmgSev === "critical" || dmgSev === "major" ? "#ef4444" : "#f59e0b"}`,
                          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.03)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        {/* Top Header Row */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                            {/* Card Number Index Badge */}
                            <span
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "8px",
                                backgroundColor: dmgSev === "critical" || dmgSev === "major" ? "#fee2e2" : "#fef3c7",
                                color: dmgSev === "critical" || dmgSev === "major" ? "#991b1b" : "#92400e",
                                fontWeight: 800,
                                fontSize: "13.5px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: dmgSev === "critical" || dmgSev === "major" ? "1px solid #fca5a5" : "1px solid #fde68a",
                              }}
                            >
                              {idx + 1}
                            </span>

                            {/* Damage Type Tag Badge */}
                            <span
                              style={{
                                fontSize: "11.5px",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                backgroundColor: isAdmin ? "#fee2e2" : "#fde68a",
                                color: isAdmin ? "#991b1b" : "#713f12",
                                border: isAdmin ? "1px solid #fca5a5" : "none",
                                padding: "4px 10px",
                                borderRadius: "6px",
                                letterSpacing: "0.04em",
                              }}
                            >
                              {dmgType}
                            </span>

                            {isAdmin && (
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 800,
                                  backgroundColor: "#fee2e2",
                                  color: "#dc2626",
                                  border: "1px solid #fca5a5",
                                  padding: "3px 8px",
                                  borderRadius: "12px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                👤 ADMIN MARKED
                              </span>
                            )}

                            {/* Title */}
                            <strong style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>
                              {dmgLoc}
                            </strong>
                          </div>

                          {/* Top-Right Badges */}
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span
                              style={{
                                fontSize: "11.5px",
                                fontWeight: 700,
                                color: dmgSev === "critical" || dmgSev === "major" ? "#dc2626" : "#d97706",
                                backgroundColor: dmgSev === "critical" || dmgSev === "major" ? "#fee2e2" : "#fef3c7",
                                padding: "4px 10px",
                                borderRadius: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <AlertCircle size={13} />
                              Severity: {dmgSev.toUpperCase()}
                            </span>

                            {isAdmin ? (
                              <span
                                style={{
                                  fontSize: "11.5px",
                                  fontWeight: 700,
                                  color: "#991b1b",
                                  backgroundColor: "#fee2e2",
                                  padding: "4px 10px",
                                  borderRadius: "12px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <CheckCircle2 size={13} color="#991b1b" />
                                Admin Verified (100%)
                              </span>
                            ) : (
                              dmgConf && (
                                <span
                                  style={{
                                    fontSize: "11.5px",
                                    fontWeight: 700,
                                    color: "#1d4ed8",
                                    backgroundColor: "#dbeafe",
                                    padding: "4px 10px",
                                    borderRadius: "12px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <Sparkles size={13} />
                                  {dmgConf} AI Confidence
                                </span>
                              )
                            )}

                            {(() => {
                              const StatusIcon = statusCfg.IconComp;
                              return (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 800,
                                    color: statusCfg.color,
                                    backgroundColor: statusCfg.bgColor,
                                    border: `1px solid ${statusCfg.borderColor}`,
                                    padding: "3.5px 10px",
                                    borderRadius: "12px",
                                    letterSpacing: "0.02em",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                  }}
                                >
                                  <StatusIcon size={13} color={statusCfg.color} />
                                  <span>{statusCfg.label}</span>
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Description & Dynamic Rule Behavior Note */}
                        <p style={{ margin: 0, fontSize: "13px", color: "#475569", lineHeight: "1.5" }}>
                          {dmgDesc}
                        </p>



                        {statusCfg.subtext && (
                          <div
                            style={{
                              fontSize: "11.5px",
                              fontWeight: 600,
                              color: statusCfg.color,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: statusCfg.bgColor,
                              padding: "4px 10px",
                              borderRadius: "6px",
                              border: `1px solid ${statusCfg.borderColor}`,
                              width: "fit-content",
                            }}
                          >
                            <Info size={13} />
                            <span>Rule Action: {statusCfg.subtext}</span>
                          </div>
                        )}

                        <div style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          {imgNums.length > 0 && (
                            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                              <Camera size={13} />
                              <span>Flagged in Angle Photo(s): #{imgNums.join(", #")}</span>
                            </div>
                          )}
                          <span style={{ color: "#cbd5e1" }}>•</span>
                          <div>
                            Vehicle / Car: <strong style={{ color: "#0f172a" }}>{dmg.vehicle_number || currentIncident.vehicle_number || "NU74 YBF"}</strong>
                          </div>
                        </div>

                        {/* Bottom Row: Photos Gallery (Left) + 5 Action Buttons Box (Right) */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                            gap: "16px",
                            flexWrap: "wrap",
                            marginTop: "6px",
                            paddingTop: "12px",
                            borderTop: "1px dashed #e2e8f0",
                          }}
                        >
                          {/* Damage Photos & Angle Views Gallery */}
                          {(() => {
                            const dmgPhotos = getPhotosForDamage(dmg, currentIncident);
                            if (dmgPhotos.length === 0) return <div />;

                            return (
                              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                  {dmgPhotos.map((photo, pIdx) => (
                                    <div
                                      key={pIdx}
                                      onClick={(e) => {
              e.stopPropagation();
                                        if (photo.angleLabel) {
                                          setSelectedAngle(photo.angleLabel);
                                        }
                                        openCompareModal("current");
                                      }}
                                      style={{
                                        position: "relative",
                                        width: "120px",
                                        height: "85px",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        border: "2px solid #cbd5e1",
                                        backgroundColor: "#0f172a",
                                        cursor: "pointer",
                                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                                        transition: "all 0.2s ease",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = "#ffcb0b";
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = "#cbd5e1";
                                        e.currentTarget.style.transform = "translateY(0)";
                                      }}
                                      title={`Click to open ${photo.label} in full screen`}
                                    >
                                      <img
                                        src={photo.url}
                                        alt={photo.label}
                                        referrerPolicy="no-referrer"
                                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                      />
                                      <div
                                        style={{
                                          position: "absolute",
                                          bottom: 0,
                                          left: 0,
                                          right: 0,
                                          backgroundColor: "rgba(15, 23, 42, 0.85)",
                                          color: "#ffffff",
                                          fontSize: "10px",
                                          fontWeight: 700,
                                          padding: "2px 4px",
                                          textAlign: "center",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {photo.label}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          {/* 5 Action Buttons Toolbar Box (Highlighted Red Box in Image 2) */}
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap",
                              backgroundColor: "#f8fafc",
                              border: "1.5px solid #cbd5e1",
                              borderRadius: "14px",
                              padding: "8px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            }}
                          >
                            {/* Button 1: Resolved after repair */}
                            <button
                              type="button"
                              onClick={() => handleDefectActionChange(idx, dmg, "resolved_after_repair")}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "10px 16px",
                                borderRadius: "10px",
                                backgroundColor: activeAction === "resolved_after_repair" || activeAction === "resolved" ? "#15803d" : "#40b26b",
                                color: "#ffffff",
                                border: activeAction === "resolved_after_repair" || activeAction === "resolved" ? "2px solid #14532d" : "none",
                                fontSize: "12px",
                                fontWeight: 700,
                                cursor: "pointer",
                                boxShadow: activeAction === "resolved_after_repair" || activeAction === "resolved" ? "0 0 0 3px rgba(34, 197, 94, 0.4)" : "0 2px 4px rgba(0,0,0,0.05)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <CheckCircle2 size={18} color="#ffffff" />
                              <span style={{ textAlign: "left", lineHeight: 1.2 }}>
                                Resolved<br />after repair
                              </span>
                            </button>

                            {/* Button 2: Booked in for repair */}
                            <button
                              type="button"
                              onClick={(e) => {
              e.stopPropagation();
                                const d = prompt("Enter Repair Date (YYYY-MM-DD):", repairDates[idx] || "");
                                handleDefectActionChange(idx, dmg, "booked_in_for_repair", d || undefined);
                              }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 14px",
                                borderRadius: "10px",
                                backgroundColor: activeAction === "booked_in_for_repair" || activeAction === "booked" ? "#bfdbfe" : "#dbeafe",
                                color: "#1d4ed8",
                                border: activeAction === "booked_in_for_repair" || activeAction === "booked" ? "2px solid #1e40af" : "1px solid #bfdbfe",
                                fontSize: "12px",
                                fontWeight: 700,
                                cursor: "pointer",
                                textAlign: "left",
                                boxShadow: activeAction === "booked_in_for_repair" || activeAction === "booked" ? "0 0 0 3px rgba(59, 130, 246, 0.4)" : "0 2px 4px rgba(0,0,0,0.03)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <Calendar size={16} color="#1d4ed8" />
                              <div>
                                <div style={{ fontSize: "12px", fontWeight: 700, lineHeight: 1.2 }}>Booked in for repair</div>
                                <div style={{ fontSize: "10px", fontWeight: 600, color: "#2563eb", marginTop: "2px", display: "flex", alignItems: "center", gap: "2px" }}>
                                  <span>{repairDates[idx] ? `Date: ${repairDates[idx]}` : "Select repair date"}</span>
                                  <ChevronRight size={10} />
                                </div>
                              </div>
                            </button>

                            {/* Button 3: Ignore Once */}
                            <button
                              type="button"
                              onClick={() => handleDefectActionChange(idx, dmg, "ignore_once")}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "10px 14px",
                                borderRadius: "10px",
                                backgroundColor: activeAction === "ignore_once" ? "#cbd5e1" : "#e2e8f0",
                                color: "#334155",
                                border: activeAction === "ignore_once" ? "2px solid #475569" : "none",
                                fontSize: "12px",
                                fontWeight: 700,
                                cursor: "pointer",
                                boxShadow: activeAction === "ignore_once" ? "0 0 0 3px rgba(100, 116, 139, 0.3)" : "0 2px 4px rgba(0,0,0,0.03)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <EyeOff size={16} color="#334155" />
                              <span>Ignore Once</span>
                            </button>

                            {/* Button 4: Ignore Permanently */}
                            <button
                              type="button"
                              onClick={() => handleDefectActionChange(idx, dmg, "ignore_permanently")}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "10px 14px",
                                borderRadius: "10px",
                                backgroundColor: activeAction === "ignore_permanently" || activeAction === "ignore_perm" ? "#1e293b" : "#475569",
                                color: "#ffffff",
                                border: activeAction === "ignore_permanently" || activeAction === "ignore_perm" ? "2px solid #0f172a" : "none",
                                fontSize: "12px",
                                fontWeight: 700,
                                cursor: "pointer",
                                boxShadow: activeAction === "ignore_permanently" || activeAction === "ignore_perm" ? "0 0 0 3px rgba(71, 85, 105, 0.4)" : "0 2px 4px rgba(0,0,0,0.05)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <Ban size={16} color="#ffffff" />
                              <span>Ignore Permanently</span>
                            </button>

                            {/* Button 5: Confirmed Damage */}
                            <button
                              type="button"
                              onClick={() => handleDefectActionChange(idx, dmg, "confirmed_damage")}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "10px 14px",
                                borderRadius: "10px",
                                backgroundColor: activeAction === "confirmed_damage" || activeAction === "confirmed" ? "#b91c1c" : "#ef4444",
                                color: "#ffffff",
                                border: activeAction === "confirmed_damage" || activeAction === "confirmed" ? "2px solid #7f1d1d" : "none",
                                fontSize: "12px",
                                fontWeight: 700,
                                cursor: "pointer",
                                boxShadow: activeAction === "confirmed_damage" || activeAction === "confirmed" ? "0 0 0 3px rgba(239, 68, 68, 0.4)" : "0 2px 4px rgba(0,0,0,0.05)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <AlertTriangle size={16} color="#ffffff" />
                              <span>Confirmed Damage</span>
                            </button>

                         
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

                        {/* Driver Note Display Card */}
            {(() => {
              const driverNote = detailData?.incident_data?.notes || (detailData?.incident_data as any)?.driver_notes;
              return (
                <section className={`${styles.card}`} style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      padding: "14px 16px",
                      borderRadius: "10px",
                      backgroundColor: driverNote && driverNote.trim() ? "#fffbeb" : "#f8fafc",
                      border: driverNote && driverNote.trim() ? "1px solid #fde68a" : "1px solid #e2e8f0",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                      <strong style={{ fontSize: "14px", color: "#451a03", display: "flex", alignItems: "center", gap: "6px" }}>
                        <FileText size={16} /> Driver Note:
                      </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: "13px", color: driverNote && driverNote.trim() ? "#78350f" : "#64748b", fontStyle: driverNote && driverNote.trim() ? "normal" : "italic" }}>
                      {driverNote && driverNote.trim() ? driverNote : "No notes provided by driver."}
                    </p>
                  </div>
                </section>
              );
            })()}

            {/* Manager Actions */}
            <section className={`${styles.card} ${styles.actionsCard}`}>
              <h3 className={styles.cardTitle}>Manager Actions &amp; Review Notes</h3>

              {saveMessage && (
                <div
                  style={{
                    margin: "12px 0 6px 0",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#dcfce7",
                    border: "1px solid #86efac",
                    color: "#166534",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {saveMessage}
                </div>
              )}

              {/* Live Display Card for Saved Admin Notes */}
              <div
                style={{
                  margin: "14px 0",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  backgroundColor: notes && notes.trim() ? "#eff6ff" : "#f8fafc",
                  border: notes && notes.trim() ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                  <strong style={{ fontSize: "13px", color: "#1e293b", display: "flex", alignItems: "center", gap: "6px" }}>
                    📝 Saved Admin Note:
                  </strong>
                  <span style={{ fontSize: "11.5px", fontWeight: 700, padding: "2px 8px", borderRadius: "8px", background: "#dbeafe", color: "#1d4ed8" }}>
                    {override}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: "13px", color: notes && notes.trim() ? "#1e3a8a" : "#64748b", fontStyle: notes && notes.trim() ? "normal" : "italic" }}>
                  {notes && notes.trim() ? notes : "No admin notes added yet. Enter notes below and click 'Save Review'."}
                </p>
              </div>

              <label className={styles.fieldLabel}>Edit Admin Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter admin or manager inspection notes here..."
                rows={3}
                className={styles.textarea}
              />

              <label className={styles.fieldLabel}>Override AI</label>
              <select
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                className={styles.select}
              >
                <option value="confirmed">Confirmed</option>
                <option value="Accept AI">Accept AI</option>
                <option value="Reject AI">Reject AI</option>
                <option value="Flag for review">Flag for review</option>
              </select>

              <div className={styles.buttonRow}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleSaveReview}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Review"}
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={() => detailData && exportFleetInspectionPDF(detailData, notes, override)}
                >
                  <FileText size={16} />
                  Export PDF
                </button>
              </div>
            </section>
          </>
        )}

        {/* Large Side-by-Side Comparison Box Modal */}
        {isCompareModalOpen && (
          <div className={styles.modalOverlay} onClick={() => setIsCompareModalOpen(false)}>
            <div
              className={`${styles.modalContent} ${isModalFullScreen ? styles.modalContentFullScreen : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={styles.modalHeader}>
                <div>
                  <h3 className={styles.modalTitle}>
                    <Layers size={18} color="#ffcb0b" />
                    Side-by-Side Inspection Comparison ({selectedAngle})
                  </h3>
                  <p className={styles.modalSubtitle}>
                    Vehicle: <strong>{currentIncident?.vehicle_number || "N/A"}</strong> • Driver: <strong>{currentIncident?.driver_name || "N/A"}</strong>
                  </p>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    type="button"
                    className={styles.modalHeaderActionBtn}
                    onClick={() => setIsModalFullScreen(!isModalFullScreen)}
                    title={isModalFullScreen ? "Exit Full Screen" : "Full Screen Mode"}
                  >
                    {isModalFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    <span>{isModalFullScreen ? "Exit Full Screen" : "Full Screen"}</span>
                  </button>

                  <button
                    type="button"
                    className={styles.modalCloseBtn}
                    onClick={() => setIsCompareModalOpen(false)}
                    title="Close (Esc)"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Modal Toolbar: View Modes, Zoom ON/OFF Toggle Switch & 8-Angle Selection Filters */}
              <div className={styles.modalToolbar}>
                {/* Row 1: View Modes & Zoom ON/OFF Toggle Switch */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", width: "100%", paddingBottom: "8px", borderBottom: "1px solid #1e293b" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginRight: "6px" }}>
                      View Mode:
                    </span>
                    <button
                      type="button"
                      onClick={() => setModalMode("compare")}
                      className={`${styles.toolBtn} ${modalMode === "compare" ? styles.toolBtnActive : ""}`}
                    >
                      <Layers size={14} /> Side-by-Side Comparison
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalMode("previous")}
                      className={`${styles.toolBtn} ${modalMode === "previous" ? styles.toolBtnActive : ""}`}
                    >
                      📷 Previous Inspection
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalMode("current")}
                      className={`${styles.toolBtn} ${modalMode === "current" ? styles.toolBtnActive : ""}`}
                    >
                      📸 Current Inspection
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalMode("summary")}
                      className={`${styles.toolBtn} ${modalMode === "summary" ? styles.toolBtnActive : ""}`}
                    >
                      📋 Inspection Summary
                    </button>
                  </div>

                  {/* Sleek Zoom ON/OFF Toggle Switch matching Website Design */}
                  {modalMode !== "summary" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "12.5px", fontWeight: 600, color: "#cbd5e1" }}>
                        🔍 Cursor Zoom:
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsZoomEnabled(!isZoomEnabled)}
                        title={isZoomEnabled ? "Disable cursor zoom on hover" : "Enable cursor zoom on hover"}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "5px 14px",
                          borderRadius: "20px",
                          backgroundColor: isZoomEnabled ? "#ffcb0b" : "#334155",
                          color: isZoomEnabled ? "#1f1a17" : "#cbd5e1",
                          border: "none",
                          fontWeight: 700,
                          fontSize: "12px",
                          cursor: "pointer",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: isZoomEnabled ? "0 2px 8px rgba(255, 203, 11, 0.4)" : "none",
                        }}
                      >
                        <span
                          style={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor: isZoomEnabled ? "#1f1a17" : "#94a3b8",
                            transition: "all 0.2s ease",
                          }}
                        />
                        {isZoomEnabled ? "ZOOM ON" : "ZOOM OFF"}
                      </button>

                      {/* Mark Damage Button in Modal Top Toolbar */}
                      <button
                        type="button"
                        onClick={() => setIsMarkingMode(!isMarkingMode)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 14px",
                          borderRadius: "20px",
                          backgroundColor: isMarkingMode ? "#dc2626" : "rgba(220, 38, 38, 0.15)",
                          color: isMarkingMode ? "#ffffff" : "#fca5a5",
                          border: "1.5px solid #dc2626",
                          fontSize: "12px",
                          fontWeight: 800,
                          cursor: "pointer",
                          boxShadow: isMarkingMode ? "0 0 0 3px rgba(220, 38, 38, 0.35)" : "none",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isMarkingMode ? (
                          <>
                            <X size={14} />
                            <span>Cancel Marking</span>
                          </>
                        ) : (
                          <>
                            <PlusCircle size={14} />
                            <span>+ Mark Damage</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Row 2: 8-Angle Selection Filters */}
                {modalMode !== "summary" && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", overflowX: "auto", paddingTop: "8px", width: "100%" }}>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginRight: "6px", whiteSpace: "nowrap" }}>
                      Select Angle:
                    </span>
                    {ANGLE_CONFIG.map((angle) => {
                      const isActive = selectedAngle === angle.label;
                      const angleDamage = getDamageForAngle(angle.label, currentIncident);
                      const isDamage = !!angleDamage;

                      return (
                        <button
                          key={angle.label}
                          type="button"
                          onClick={() => setSelectedAngle(angle.label)}
                          className={`${styles.toolBtn} ${isActive ? styles.toolBtnActive : ""}`}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: isDamage ? "#ef4444" : "#10b981",
                              display: "inline-block",
                            }}
                          />
                          {angle.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Body */}
              <div className={styles.modalBody}>
                {modalMode === "compare" && (
                  <div className={styles.modalCompareCanvas}>
                    {/* Previous Inspection */}
                    <div className={styles.modalCompareBox}>
                      <div className={styles.modalCompareTitle}>
                        📷 Previous Inspection {oldIncident ? `(${formatDateDDMMYYYY(oldIncident.inspection_date)}) — ${selectedAngle}` : `— Initial Inspection`}
                      </div>
                      <div className={styles.modalCompareImgWrap}>
                        {oldIncident ? (
                          <ModalCursorZoomImage
                            src={oldImg}
                            alt="Previous Inspection"
                            zoomEnabled={isZoomEnabled}
                            damageInfo={{ hasDamage: oldAngleMarkers.length > 0 }}
                            markers={oldAngleMarkers}
                          />
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", textAlign: "center", padding: "24px" }}>
                            <CheckCircle2 size={40} color="#10b981" style={{ marginBottom: "12px" }} />
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#f8fafc" }}>
                              Initial Inspection Record
                            </span>
                            <span style={{ fontSize: "12px", marginTop: "6px", color: "#94a3b8", maxWidth: "280px" }}>
                              No previous inspection history found for vehicle <strong>{currentIncident?.vehicle_number || "N/A"}</strong>.
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Current Inspection */}
                    <div className={styles.modalCompareBox}>
                      <div className={styles.modalCompareTitle} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>📸 Current Inspection ({formatDateDDMMYYYY(currentIncident?.inspection_date)}) — {selectedAngle}</span>
                        <button
                          type="button"
                          onClick={() => setIsMarkingMode(!isMarkingMode)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "4px 10px",
                            borderRadius: "14px",
                            backgroundColor: isMarkingMode ? "#dc2626" : "rgba(239, 68, 68, 0.2)",
                            color: isMarkingMode ? "#ffffff" : "#fca5a5",
                            border: "1px solid #ef4444",
                            fontSize: "11px",
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          {isMarkingMode ? <X size={12} /> : <PlusCircle size={12} />}
                          <span>{isMarkingMode ? "Cancel" : "+ Mark"}</span>
                        </button>
                      </div>
                      {isMarkingMode && (
                        <div style={{ padding: "6px 12px", backgroundColor: "rgba(220, 38, 38, 0.25)", borderBottom: "1px dashed #ef4444", color: "#fca5a5", fontSize: "11.5px", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
                          <MousePointer size={14} color="#ef4444" />
                          <span>Click & drag on the car image to draw a Red Box around the damage.</span>
                        </div>
                      )}
                      <div className={styles.modalCompareImgWrap}>
                        <ModalCursorZoomImage
                          src={currentImg}
                          alt="Current Inspection"
                          zoomEnabled={isMarkingMode ? false : isZoomEnabled}
                          damageInfo={{
                            hasDamage: currentAngleMarkers.length > 0 || !!getDamageForAngle(selectedAngle, currentIncident),
                            count: currentAngleMarkers.length || (getDamageForAngle(selectedAngle, currentIncident) ? 1 : 0),
                          }}
                          markers={currentAngleMarkers}
                          isMarkingMode={isMarkingMode}
                          onBoxDrawn={handleBoxDrawn}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {modalMode === "previous" && (
                  <div className={styles.modalCompareBox} style={{ width: "100%", height: "100%" }}>
                    <div className={styles.modalCompareTitle}>
                      📷 Previous Inspection Photo {oldIncident ? `(${formatDateDDMMYYYY(oldIncident.inspection_date)}) — ${selectedAngle}` : `— Initial Inspection`}
                    </div>
                    <div className={styles.modalCompareImgWrap}>
                      {oldIncident ? (
                        <ModalCursorZoomImage
                          src={oldImg}
                          alt="Previous Inspection"
                          zoomEnabled={isZoomEnabled}
                          damageInfo={{ hasDamage: oldAngleMarkers.length > 0 }}
                          markers={oldAngleMarkers}
                        />
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", textAlign: "center", padding: "24px" }}>
                          <CheckCircle2 size={40} color="#10b981" style={{ marginBottom: "12px" }} />
                          <span style={{ fontSize: "14px", fontWeight: 700, color: "#f8fafc" }}>
                            Initial Inspection Record
                          </span>
                          <span style={{ fontSize: "12px", marginTop: "6px", color: "#94a3b8", maxWidth: "280px" }}>
                            No previous inspection history found for vehicle <strong>{currentIncident?.vehicle_number || "N/A"}</strong>.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {modalMode === "current" && (
                  <div className={styles.modalCompareBox} style={{ width: "100%", height: "100%" }}>
                    <div className={styles.modalCompareTitle} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>📸 Current Inspection Photo ({formatDateDDMMYYYY(currentIncident?.inspection_date)}) — {selectedAngle}</span>
                      <button
                        type="button"
                        onClick={() => setIsMarkingMode(!isMarkingMode)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "4px 10px",
                          borderRadius: "14px",
                          backgroundColor: isMarkingMode ? "#dc2626" : "rgba(239, 68, 68, 0.2)",
                          color: isMarkingMode ? "#ffffff" : "#fca5a5",
                          border: "1px solid #ef4444",
                          fontSize: "11px",
                          fontWeight: 800,
                          cursor: "pointer",
                        }}
                      >
                        {isMarkingMode ? <X size={12} /> : <PlusCircle size={12} />}
                        <span>{isMarkingMode ? "Cancel" : "+ Mark"}</span>
                      </button>
                    </div>
                    {isMarkingMode && (
                      <div style={{ padding: "6px 12px", backgroundColor: "rgba(220, 38, 38, 0.25)", borderBottom: "1px dashed #ef4444", color: "#fca5a5", fontSize: "11.5px", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
                        <MousePointer size={14} color="#ef4444" />
                        <span>Click & drag on the car image to draw a Red Box around the damage.</span>
                      </div>
                    )}
                    <div className={styles.modalCompareImgWrap}>
                      <ModalCursorZoomImage
                        src={currentImg}
                        alt="Current Inspection"
                        zoomEnabled={isMarkingMode ? false : isZoomEnabled}
                        damageInfo={{
                          hasDamage: currentAngleMarkers.length > 0 || !!getDamageForAngle(selectedAngle, currentIncident),
                          count: currentAngleMarkers.length || (getDamageForAngle(selectedAngle, currentIncident) ? 1 : 0),
                        }}
                        markers={currentAngleMarkers}
                        isMarkingMode={isMarkingMode}
                        onBoxDrawn={handleBoxDrawn}
                      />
                    </div>
                  </div>
                )}

                {modalMode === "summary" && (
                  <div className={styles.modalSummaryContent}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#ffcb0b", marginBottom: "16px" }}>
                      📋 Complete Inspection &amp; Damage Summary
                    </h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                      <div style={{ background: "#1e293b", padding: "14px", borderRadius: "10px", border: "1px solid #334155" }}>
                        <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase" }}>Vehicle Number</span>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#ffffff" }}>{currentIncident?.vehicle_number || "N/A"}</div>
                      </div>
                      <div style={{ background: "#1e293b", padding: "14px", borderRadius: "10px", border: "1px solid #334155" }}>
                        <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase" }}>Driver Name</span>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#ffffff" }}>{currentIncident?.driver_name || "N/A"}</div>
                      </div>
                      <div style={{ background: "#1e293b", padding: "14px", borderRadius: "10px", border: "1px solid #334155" }}>
                        <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase" }}>Inspection Date</span>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#ffffff" }}>{formatDateDDMMYYYY(currentIncident?.inspection_date)}</div>
                      </div>
                      <div style={{ background: "#1e293b", padding: "14px", borderRadius: "10px", border: "1px solid #334155" }}>
                        <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase" }}>Damage Points</span>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#ef4444" }}>{currentIncident?.damage_points ?? 0} pts</div>
                      </div>
                    </div>

                    {/* AI Classified Damages Table inside Summary Modal */}
                    <div style={{ background: "#1e293b", padding: "16px", borderRadius: "10px", border: "1px solid #334155", marginBottom: "16px" }}>
                      <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#f8fafc", margin: "0 0 12px 0" }}>
                        AI Classified Damages ({currentIncident?.damages?.length || 0})
                      </h4>
                      {!currentIncident?.damages || currentIncident.damages.length === 0 ? (
                        <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8" }}>No damages detected for this inspection.</p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {currentIncident.damages.map((d: any, i: number) => (
                            <div key={i} style={{ padding: "12px", background: "#0f172a", borderRadius: "8px", borderLeft: "3px solid #ef4444" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                                <span style={{ fontSize: "12px", fontWeight: 700, color: "#ffcb0b", textTransform: "uppercase" }}>
                                  {d.damage_type || d.type} — {d.location}
                                </span>
                                <span style={{ fontSize: "11px", color: "#38bdf8", fontWeight: 600 }}>
                                  {d.severity?.toUpperCase()} • {d.confidence}% Confidence
                                </span>
                              </div>
                              <p style={{ margin: 0, fontSize: "12.5px", color: "#cbd5e1" }}>{d.description}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ background: "#1e293b", padding: "16px", borderRadius: "10px", border: "1px solid #334155" }}>
                      <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#f8fafc", margin: "0 0 10px 0" }}>Saved Manager Review Notes</h4>
                      <p style={{ margin: 0, fontSize: "13.5px", color: notes ? "#cbd5e1" : "#94a3b8", fontStyle: notes ? "normal" : "italic" }}>
                        {notes && notes.trim() ? notes : "No notes recorded for this inspection yet."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Admin Manual Mark Damage Modal Dialog */}
        {isAddDamageModalOpen && drawnBox && currentIncident && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(15, 23, 42, 0.75)",
              backdropFilter: "blur(4px)",
              zIndex: 100005,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isSubmittingDamage) {
                setIsAddDamageModalOpen(false);
                setDrawnBox(null);
              }
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "520px",
                backgroundColor: "#ffffff",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  backgroundColor: "#0f172a",
                  padding: "16px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "#ffffff",
                  borderBottom: "2px solid #ef4444",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "rgba(239, 68, 68, 0.2)",
                      border: "1px solid #ef4444",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlusCircle size={18} color="#ef4444" />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#ffffff" }}>
                      Mark New Vehicle Damage
                    </h3>
                    <span style={{ fontSize: "11.5px", color: "#94a3b8", fontWeight: 500 }}>
                      Admin manual override for missed damage
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
              e.stopPropagation();
                    setIsAddDamageModalOpen(false);
                    setDrawnBox(null);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#94a3b8",
                    cursor: "pointer",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>
                {/* Meta details banner */}
                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "10px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Vehicle Plate</span>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#0f172a" }}>
                      {currentIncident.vehicle_number || "NU74 YBF"}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Photo Frame</span>
                    <div style={{ fontSize: "13px", fontWeight: 800, color: "#dc2626" }}>
                      #{selectedConfig.index} — {selectedConfig.label}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Box Area</span>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: "#475569" }}>
                      {Math.round(drawnBox.width * 100)}% × {Math.round(drawnBox.height * 100)}%
                    </div>
                  </div>
                </div>

                {/* Damage Type Selection */}
                <div>
                  <label style={{ display: "block", fontSize: "12.5px", fontWeight: 700, color: "#1e293b", marginBottom: "8px" }}>
                    Damage Type <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {["SCUFF", "SCRATCH", "DENT", "PAINT DAMAGE", "CRACK", "PANEL DAMAGE"].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setNewDamageType(t)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer",
                          backgroundColor: newDamageType === t ? "#fee2e2" : "#f1f5f9",
                          color: newDamageType === t ? "#991b1b" : "#475569",
                          border: newDamageType === t ? "1.5px solid #ef4444" : "1px solid #cbd5e1",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Short Message / Description */}
                <div>
                  <label style={{ display: "block", fontSize: "12.5px", fontWeight: 700, color: "#1e293b", marginBottom: "6px" }}>
                    Short Message / Description <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <textarea
                    rows={3}
                    value={newDamageDescription}
                    onChange={(e) => setNewDamageDescription(e.target.value)}
                    placeholder="e.g., Lower door par scuff hai - missed by AI"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                  />
                  <span style={{ fontSize: "11px", color: "#64748b", marginTop: "3px", display: "block" }}>
                    This note will appear directly in the Breakdown list and on the vehicle frame.
                  </span>
                </div>

                {/* Severity Selection */}
                <div>
                  <label style={{ display: "block", fontSize: "12.5px", fontWeight: 700, color: "#1e293b", marginBottom: "8px" }}>
                    Severity Level
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
                    {[
                      { val: "minor", label: "Minor", bg: "#fef3c7", color: "#92400e" },
                      { val: "medium", label: "Medium", bg: "#ffedd5", color: "#9a3412" },
                      { val: "major", label: "Major", bg: "#fee2e2", color: "#991b1b" },
                      { val: "critical", label: "Critical", bg: "#ffe4e6", color: "#881337" },
                    ].map((s) => (
                      <button
                        key={s.val}
                        type="button"
                        onClick={() => setNewDamageSeverity(s.val)}
                        style={{
                          padding: "8px 4px",
                          borderRadius: "8px",
                          fontSize: "11.5px",
                          fontWeight: 800,
                          textTransform: "uppercase",
                          cursor: "pointer",
                          backgroundColor: newDamageSeverity === s.val ? s.bg : "#ffffff",
                          color: newDamageSeverity === s.val ? s.color : "#64748b",
                          border: newDamageSeverity === s.val ? `2px solid ${s.color}` : "1px solid #cbd5e1",
                          textAlign: "center",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "14px 20px",
                  backgroundColor: "#f8fafc",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
              e.stopPropagation();
                    setIsAddDamageModalOpen(false);
                    setDrawnBox(null);
                  }}
                  disabled={isSubmittingDamage}
                  style={{
                    padding: "9px 18px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    color: "#475569",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAdminDamage}
                  disabled={isSubmittingDamage}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "9px 20px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#dc2626",
                    color: "#ffffff",
                    fontSize: "12.5px",
                    fontWeight: 800,
                    cursor: isSubmittingDamage ? "not-allowed" : "pointer",
                    boxShadow: "0 2px 6px rgba(220, 38, 38, 0.4)",
                  }}
                >
                  <Check size={16} />
                  <span>{isSubmittingDamage ? "Saving..." : "Save Damage Marker"}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DamageAnalysis;




